import { useCachedData } from '../hooks/useCachedData';
import { endpoints } from '../api/config';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import DashboardLayout from '../components/DashboardLayout';
import { useState, useEffect, useMemo } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { Box } from '@mui/material';

const formatHeader = (key) =>
  key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const normalizeLocationsRows = (data) => {
  if (data == null) return [];
  if (Array.isArray(data)) return data;

  if (typeof data === 'object') {
    const keys = Object.keys(data);
    if (keys.length === 0) return [];

    const allNumericKeys = keys.every((k) => !Number.isNaN(Number(k)));
    if (allNumericKeys) {
      return Object.entries(data).map(([key, value]) =>
        value && typeof value === 'object' && !Array.isArray(value)
          ? { id: key, ...value }
          : { id: key, value }
      );
    }

    if (data.data != null) {
      return normalizeLocationsRows(data.data);
    }

    const values = Object.values(data).filter((v) => v != null);
    if (values.length === 1 && Array.isArray(values[0])) {
      return values[0];
    }
    if (values.length && values.every((v) => typeof v === 'object' && !Array.isArray(v))) {
      return values;
    }
  }

  return [];
};

const Locations = () => {
  const { data, isLoading, error, refetch } = useCachedData(endpoints.locations);
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('locations-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    localStorage.setItem('locations-column-visibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  const tableData = useMemo(() => normalizeLocationsRows(data), [data]);

  const columns = useMemo(() => {
    const preferredOrder = ['id', 'location_id', 'internal_id', 'location', 'name'];
    if (!tableData.length) {
      return preferredOrder.map((accessorKey) => ({
        accessorKey,
        header: formatHeader(accessorKey),
        minSize: 120,
        size: 180,
        enableSorting: true,
      }));
    }

    const keys = new Set();
    tableData.forEach((row) => {
      Object.keys(row || {}).forEach((k) => keys.add(k));
    });

    const rest = Array.from(keys)
      .filter((k) => !preferredOrder.includes(k))
      .sort();

    const orderedKeys = [...preferredOrder.filter((k) => keys.has(k)), ...rest];

    return orderedKeys.map((key) => ({
      accessorKey: key,
      header: formatHeader(key),
      minSize: 120,
      size: Math.min(280, Math.max(140, formatHeader(key).length * 9)),
      enableSorting: true,
      cell: (info) => {
        const value = info.getValue();
        if (value === null || value === undefined || value === '') return '-';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
      },
    }));
  }, [tableData]);

  const initialSorting = useMemo(() => {
    const first = columns[0]?.accessorKey;
    return first ? [{ id: first, desc: false }] : [];
  }, [columns]);

  const appGrid = useDataGridProTable({
    data: tableData,
    loading: isLoading,
    error: error ? error.message || 'Failed to load locations' : null,
    title: 'Locations',
    columns,
    onDataRefresh: refetch,
    initialColumnVisibility: columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    initialSorting,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    muiTableContainerProps: { sx: { height: '100%' } },
  });

  return (
    <DashboardLayout>
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <MuiXDataGridSurface hook={appGrid} DataGridPro={DataGridPro} />
      </Box>
    </DashboardLayout>
  );
};

export default Locations;
